import { debounce } from "../../utils/debounce.js";
import { setDisabled } from "../../utils/domUtils.js";
import {
  loadTreeSearchProperties,
  searchQuery,
  fetchLiveTreeSearch,
  fetchLiveAreaSearch,
  loadAreasForSearch,
} from "../../searchFunctions.js";
import { setStatistikDaten } from "../../statisticFunctions.js";
import {
  zuruecksetzenLiveSuche,
  zuruecksetzenLiveSucheFlaechen,
} from "../../menuFunctions.js";
import { removeMapLayer, zeigeAlleFlaechenAn } from "../../mapFunctions.js";
import { warningMessage } from "../../helperFunctions.js";
import { getActualMap } from "../../globalContext.js";
import {
  renderTreeSearchDropdown,
  renderLiveSearchResults,
  renderAreaLiveSearchResults,
} from "./searchUI.js";

// DOM-Elemente
const eigenschaftenDropdown = document.getElementById("baumEigenschaft");
const searchInputField = document.getElementById("searchInputField");
const searchButton = document.getElementById("searchButton");
const deleteButton = document.getElementById("delete-search-button");
const mehrSuchergebnisse = document.getElementById("mehrSuchergebnisse");

// Flächensuche-Elemente (optional – existieren aktuell nicht im HTML)
const flaecheSucheEingabefeld = document.getElementById("flaecheSucheEingabefeld");
const eigenschaftenFlaechenDropdown = document.getElementById("flaecheEigenschaft");

// Lokaler State: alle Livesuchergebnisse für "Mehr anzeigen"
let allLiveSearchResults = null;

async function resetSearch(map) {
  setDisabled(deleteButton, true);
  setDisabled(statistikButton, true);
  setDisabled(searchButton, true);
  setDisabled(searchInputField, true);
  searchInputField.value = "";

  const eigenschaften = await loadTreeSearchProperties();
  renderTreeSearchDropdown(eigenschaften);

  document.getElementById("resultCount").innerHTML = "Anzahl angezeigter Bäume: 0";
  document.getElementById("liveSearchResultCount").innerHTML = "Anzahl gefundener Bäume: 0";
  document.getElementById("co2TonBadge").innerHTML = "0.00";

  const livesearchBox = document.getElementById("livesearch");
  livesearchBox.innerHTML = "";

  mehrSuchergebnisse.setAttribute("hidden", true);
  document.getElementById("data-table-content").innerHTML = "";

  removeMapLayer(map, "Suchergebnis");
}

export function initSearchEventHandler(map) {
  // Dropdown: Baumeigenschaft wählen
  eigenschaftenDropdown.onchange = function () {
    const hasSelection = eigenschaftenDropdown.selectedIndex !== 0;
    setDisabled(deleteButton, !hasSelection);
    setDisabled(searchInputField, !hasSelection);
    setDisabled(searchButton, true);
    searchInputField.value = "";
    if (hasSelection) searchInputField.focus();
  };

  // Event-Delegation: Klick auf Livesuchergebnis
  document.getElementById("livesearch").addEventListener("click", async function (event) {
    if (event.target.classList.contains("livesearchLink")) {
      event.preventDefault();
      document.getElementById("searchInputField").value = event.target.textContent;
      const statistikdaten = await searchQuery();
      if (statistikdaten?.features) {
        setStatistikDaten(statistikdaten.features);
        statistikButton.disabled = false;
      }
      document.getElementById("livesearch").style.display = "none";
    }
  });

  // Live-Suche mit Debounce
  const debouncedLiveSearch = debounce(async function () {
    const property =
      eigenschaftenDropdown.options[eigenschaftenDropdown.selectedIndex].text;
    const result = await fetchLiveTreeSearch(property, searchInputField.value);

    if (!result) return;

    allLiveSearchResults = result.all;
    renderLiveSearchResults(result.displayed);

    if (result.hasMore) {
      mehrSuchergebnisse.removeAttribute("hidden");
    } else {
      mehrSuchergebnisse.setAttribute("hidden", true);
    }

    const livesearch = document.getElementById("livesearch");
    if (livesearch.innerHTML !== "Keine Ergebnisse gefunden") {
      setDisabled(searchButton, false);
    }
  }, 250);

  searchInputField.addEventListener("keyup", function (event) {
    setDisabled(searchButton, true);
    setDisabled(statistikButton, true);
    zuruecksetzenLiveSuche();
    removeMapLayer(map, "Suchergebnis");
    if (event.key !== "Enter" && searchInputField.value !== "") {
      debouncedLiveSearch();
    }
  });

  // Enter-Taste im Suchfeld
  searchInputField.addEventListener("keypress", async function (event) {
    const livesearch = document.getElementById("livesearch");
    if (
      event.key === "Enter" &&
      searchInputField.value !== "" &&
      livesearch.innerHTML !== null &&
      livesearch.innerText !== "Keine Ergebnisse gefunden"
    ) {
      event.preventDefault();
      const statistikdaten = await searchQuery();
      if (statistikdaten?.features) {
        setStatistikDaten(statistikdaten.features);
        statistikButton.disabled = false;
      }
    }
  });

  // Klick ins Suchfeld: zurücksetzen
  searchInputField.onclick = function () {
    setDisabled(searchButton, true);
    statistikButton.disabled = true;
    searchInputField.value = "";
    zuruecksetzenLiveSuche();
    removeMapLayer(map, "Suchergebnis");
  };

  // Suchbutton
  searchButton.onclick = async function () {
    const statistikdaten = await searchQuery();
    if (statistikdaten?.features) {
      setStatistikDaten(statistikdaten.features);
      statistikButton.disabled = false;
    }
  };

  // Löschen-Button: Suche zurücksetzen
  deleteButton.onclick = function () {
    resetSearch(map);
  };

  // Mehr Suchergebnisse
  mehrSuchergebnisse.onclick = function () {
    if (allLiveSearchResults) {
      renderLiveSearchResults(allLiveSearchResults);
      mehrSuchergebnisse.setAttribute("hidden", true);
    }
  };

  // Flächensuche (Guard: Elemente existieren noch nicht im HTML)
  if (flaecheSucheEingabefeld && eigenschaftenFlaechenDropdown) {
    const livesearchFlaechenBox = document.getElementById("livesearchFlaechen");

    if (livesearchFlaechenBox) {
      livesearchFlaechenBox.addEventListener("click", async function (event) {
        if (event.target.classList.contains("livesearchFlaechenLink")) {
          event.preventDefault();
          flaecheSucheEingabefeld.value = event.target.textContent;
          const property =
            eigenschaftenFlaechenDropdown.options[eigenschaftenFlaechenDropdown.selectedIndex].text;
          const areas = await loadAreasForSearch(property, flaecheSucheEingabefeld.value);
          zeigeAlleFlaechenAn(areas, getActualMap().globalMap);
          livesearchFlaechenBox.style.display = "none";
        }
      });
    }

    const debouncedAreaLiveSearch = debounce(async function () {
      const property =
        eigenschaftenFlaechenDropdown.options[eigenschaftenFlaechenDropdown.selectedIndex].text;
      const result = await fetchLiveAreaSearch(property, flaecheSucheEingabefeld.value);
      if (result) {
        renderAreaLiveSearchResults(result.areas, result.countPerArea);
      }
    }, 250);

    flaecheSucheEingabefeld.addEventListener("keyup", function () {
      zuruecksetzenLiveSucheFlaechen();
      if (
        flaecheSucheEingabefeld.value !== "" &&
        eigenschaftenFlaechenDropdown.selectedIndex !== 0
      ) {
        debouncedAreaLiveSearch();
      } else {
        warningMessage("Fehler bei der Eingabe", "Klären welche Meldung, wann erscheinen soll!");
      }
    });
  }

  // Initialer Zustand: Suche zurücksetzen
  resetSearch(map);
}
